import { ApiException } from "@/domain/core/exception/exceptions"
import { emptyListResponse } from "@/domain/core/utils/django"

import order from './order'
import movement from './movement'
import serviceOrder from './serviceOrder'
import refund from './refund'
import payout from './payout'
import chargeback from '../chargeback/store'

const state = {
  // Scope: { type: 'global'|'contract-manager'|'pica-service', id?: number }
  scope: null,
  transactions: { ...emptyListResponse },
  movements: { ...emptyListResponse },
  movementsTotals: null,
}

export const getters = {
  scope: state => state.scope,
  hasScope: state => state.scope !== null,
  transactions: state => state.transactions,
  movements: state => state.movements,
  movementsTotals: state => state.movementsTotals,

  // Check if payout is available for current scope
  hasPayoutAccess: state => {
    if (!state.scope) return false
    return state.scope.type !== 'pica-service'
  },
}

export const mutations = {
  setScope(state, scope) {
    state.scope = scope
  },

  setTransactions(state, transactions) {
    state.transactions = transactions
  },

  clearTransactions(state) {
    state.transactions = { ...emptyListResponse }
  },

  setMovements(state, movements) {
    state.movements = movements
  },

  clearMovements(state) {
    state.movements = { ...emptyListResponse }
  },

  setMovementsTotals(state, totals) {
    state.movementsTotals = totals
  },

  clearMovementsTotals(state) {
    state.movementsTotals = null
  },
}

export const actions = accountingService => ({
  /**
   * Set scope and fetch initial data
   */
  async setScope({ commit }, scope) {
    commit('setScope', scope)
    commit('clearTransactions')
    commit('clearMovements')
    commit('clearMovementsTotals')

    // Also set scope in submodules
    commit('order/setScope', scope)
    commit('movement/setScope', scope)
    commit('serviceOrder/setScope', scope)
    commit('refund/setScope', scope)
    commit('chargeback/setScope', scope)
    if (scope.type !== 'pica-service') {
      commit('payout/setScope', scope)
    }
  },

  /**
   * Fetch list of transactions (admin)
   */
  async fetchTransactionList({ commit, state }, { filters, pagination, sorting }) {
    if (!state.scope) {
      console.warn('[accounting/fetchTransactionList] Scope not set, skipping')
      return
    }

    try {
      const data = await accountingService.fetchTransactionList(state.scope, filters, pagination, sorting)
      commit('setTransactions', data)
    } catch (e) {
      if (e instanceof ApiException) {
        commit('setTransactions', emptyListResponse)
      }
      throw e
    }
  },

  /**
   * Fetch list of movements (CM/PS)
   */
  async fetchMovementList({ commit, state }, { filters, pagination, sorting }) {
    if (!state.scope) {
      console.warn('[accounting/fetchMovementList] Scope not set, skipping')
      return
    }

    try {
      const data = await accountingService.fetchMovementList(state.scope, filters, pagination, sorting)
      commit('setMovements', data)
    } catch (e) {
      if (e instanceof ApiException) {
        commit('setMovements', emptyListResponse)
      }
      throw e
    }
  },

  /**
   * Fetch aggregated totals for movements (pica-service scope only)
   */
  async fetchMovementsTotals({ commit, state }, filters) {
    if (!state.scope) return
    if (state.scope.type !== 'pica-service') {
      commit('clearMovementsTotals')
      return
    }

    try {
      const data = await accountingService.fetchMovementsTotals(state.scope, filters)
      commit('setMovementsTotals', data)
    } catch (e) {
      commit('clearMovementsTotals')
    }
  },

  /**
   * Export transactions
   */
  // eslint-disable-next-line no-unused-vars
  async exportTransactions({ state }, filters) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    await accountingService.exportTransactions(state.scope, filters)
  },

  /**
   * Export movements
   */
  // eslint-disable-next-line no-unused-vars
  async exportMovements({ state }, filters) {
    if (!state.scope) {
      throw new Error('Scope not set')
    }

    await accountingService.exportMovements(state.scope, filters)
  },

  /**
   * Fetch transactions for a specific user (for UserInfo component)
   */
  // eslint-disable-next-line no-unused-vars
  async fetchUserTransactions({ state }, { userId, excludeTransactionId, limit }) {
    if (!state.scope) {
      console.warn('[accounting/fetchUserTransactions] Scope not set, skipping')
      return { results: [], total: 0 }
    }

    return await accountingService.fetchUserTransactions(state.scope, userId, excludeTransactionId, limit)
  },

  /**
   * Fetch checkins for a specific user (for UserInfo component)
   */
  // eslint-disable-next-line no-unused-vars
  async fetchUserCheckins({ state }, { userId, limit }) {
    if (!state.scope) {
      console.warn('[accounting/fetchUserCheckins] Scope not set, skipping')
      return { results: [], total: 0 }
    }

    return await accountingService.fetchUserCheckins(state.scope, userId, limit)
  },

  /**
   * Fetch recent disputes for a specific user (for RecentChargebacks component)
   */
  // eslint-disable-next-line no-unused-vars
  async fetchUserDisputes({ state }, { userId, limit, excludeId } = {}) {
    if (!state.scope) {
      console.warn('[accounting/fetchUserDisputes] Scope not set, skipping')
      return { results: [], total: 0 }
    }

    return await accountingService.fetchUserDisputes(state.scope, userId, limit, excludeId)
  },
})

export default accountingService => ({
  namespaced: true,
  state,
  getters,
  mutations,
  actions: actions(accountingService),
  modules: {
    order: order(accountingService),
    movement: movement(accountingService),
    serviceOrder: serviceOrder(accountingService),
    refund: refund(accountingService),
    payout: payout(accountingService),
    chargeback: chargeback(accountingService),
  }
})
